import React, { useState, useEffect } from 'react';

import { useAppContext } from '../context/AppContext'; // 引入 useAppContext hook

import RevisionCard from './RevisionCard';
import { TradingUnit } from '../types';

import { t } from '../i18n/i18n';
import { beUpdateUserRevision, useSound } from '../App';

//============================================================================
// 生成 Bitget 交易 URL
const generateBitgetURL = (InstId) => {
    if (!InstId) return null;
    const parts = InstId.split("-");
    if (parts.length < 3) {
        console.error("不支持的商品名格式:", InstId);
        return null;
    }
    const symbol = parts[0] + parts[1]; // 组合交易对，例如 "LTCUSDT"
    const contractType = parts[2].toUpperCase(); // "SWAP" 表示永续合约
    let marketType = "spot"; // 默认是现货
    let contractSegment = "";
    if (contractType === "SWAP") {
        marketType = "futures";
        contractSegment = "usdt"; // USDT 结算的永续合约
    } else {
        console.error("目前仅支持永续合约！");
        return null;
    }
    return `https://www.bitget.com/zh-CN/${marketType}/${contractSegment}/${symbol}`;
};

// FXIME：TradingView 交易 URL应该没这么简单，不同交易所不同商品种类，先搁置在这里吧。
const generateTradingViewURL = (InstId) => {
    // 移除 "-SWAP"（如果有）
    let symbol = InstId.replace(/-SWAP$/, "");
    // 移除所有 "-"，转换为 TradingView 格式
    symbol = symbol.replace(/-/g, "");
    // 返回 TradingView 交易对 URL
    return `https://www.tradingview.com/symbols/${symbol}/`;
};
//============================================================================
const TradingUnitStreamContainer = ({ tradingUnit, forceRenderTrigger }) => {
    //========================================================================
    // 使用 useAppContext 获取 ServiceURLPrefix
    const {
         ServiceURLPrefix,
         blockPage, unblockPage,
     } = useAppContext(); // 获取当前的服务器地址
    const [playSound] = useSound('/goldcoin.mp3');

    //========================================================================
    // 判断是否允许编辑（只有在TradingUnitStreamContainer只有一个Revision且Revision的状态为'prepare'时才允许）
    const isEditable = tradingUnit?.[0]?.status === 'prepare';

    //========================================================================
    // 用本地 state 保存 instId 和 exchange（初始值从 props.tradingUnit 中获取）
    const [localInstId, setLocalInstId] = useState(tradingUnit[0].init.instId);
    // 右键点击 InstId 打开 Bitget 链接
    const handleRightClickInstId = (event) => {
        event.preventDefault(); // 阻止默认的右键菜单
        //const url = generateTradingViewURL(localInstId);
        const url = generateBitgetURL(localInstId);
        if (url) {
            window.open(url, "_blank");
        }
    };
    // 双击instId时切换币本位和U本位合约
    const handleDoubleClickInstId = async () => {
        playSound();
        blockPage();
        // const newInstId = localInstId.replace(/-(USDT|USD)-/, (match, p1) => {
        //     return p1 === 'USDT' ? '-USD-' : '-USDT-';
        // });
        //暂时只支持永续合约SWAP，无论是合约还是现货或其他，都能通过双击切换搞成SWAP
        const newInstId = localInstId.replace(/-(USDT|USD)(-[A-Z]+)?$/, (match, p1) => {
            return p1 === 'USDT' ? '-USD-SWAP' : '-USDT-SWAP';
        });
        if( !tradingUnit[0] ) {
            unblockPage();
            throw new Error("A TradingUnit without any 'revision' is not allowed!");
        }
        if( !tradingUnit[0].init ) {
            unblockPage();
            throw new Error("A TradingUnit head revision without 'init' is not allowed!");
        }
        // 前后端操作是个事务整体
        tradingUnit[0].init.instId = newInstId;
        if( await beUpdateUserRevision(ServiceURLPrefix, tradingUnit[0]) ) {
            setLocalInstId(newInstId);//localInstId被更新是异步的！
        } else {
            tradingUnit[0].init.instId = localInstId;
            console.error("[handleDoubleClickInstId] Rollback instId. Can't update into server.");
        }
        unblockPage();
    };

    //========================================================================
    const [localExchange, setLocalExchange] = useState(tradingUnit[0].init.exchange);
    // 双击切换 exchange
    const handleDoubleClickExchange = async () => {
        playSound();
        blockPage();
        const exchanges = ['bitget demo', 'bitget real', 'okx demo', 'okx real'];
        const currentIndex = exchanges.indexOf(localExchange);
        const newEx = exchanges[(currentIndex + 1) % exchanges.length]; // 循环切换交易所    
        if (!tradingUnit[0]) {
            unblockPage();
            throw new Error("A TradingUnit without any 'revision' is not allowed!");
        }
        if (!tradingUnit[0].init) {
            unblockPage();
            throw new Error("A TradingUnit head revision without 'init' is not allowed!");
        }    
        // 前后端操作是个事务整体
        tradingUnit[0].init.exchange = newEx;
        if (await beUpdateUserRevision(ServiceURLPrefix, tradingUnit[0])) {
            setLocalExchange(newEx); // 异步的！
        } else {
            tradingUnit[0].init.exchange = localExchange;
            console.error("[handleDoubleClickExchange] Rollback exchange. Can't update into server.");
        }
        unblockPage();
    };

    //========================================================================
    // 继续保留你对 leverage 的处理或其他字段处理
    // （如果 leverage 也需要双击固定切换，就不需要 <input>，如果需要输入就保留）
    const [isEditingLeverage, setIsEditingLeverage] = useState(false);
    const [localLeverage, setLocalLeverage] = useState(tradingUnit[0].init.leverage);

    const handleDoubleClickLeverage = () => {
        playSound();
        setIsEditingLeverage(true);
    };
    const handleChangeLeverage = (e) => {
        setLocalLeverage(e.target.value);
    };
    const handleKeyDownLeverage = (e) => {
        if (e.key === 'Enter') {
            handleConfirmLeverage();
        } else if (e.key === 'Escape') {
            handleCancelLeverage();
        }
    };

    const handleConfirmLeverage = async () => {
        playSound();
        blockPage();
        setIsEditingLeverage(false);
        if (localLeverage === tradingUnit[0].init.leverage) {
            unblockPage();
            return;
        }
        if( !tradingUnit[0] ) {
            unblockPage();
            throw new Error("A TradingUnit without any 'revision' is not allowed!");
        }
        if( !tradingUnit[0].init ) {
            unblockPage();
            throw new Error("A TradingUnit head revision without 'init' is not allowed!");
        }
        // 前后端操作是个事务整体
        let oldvalue= tradingUnit[0].init.leverage;//同其他的不一样，要考虑cancel编辑和db保存失败的两种回滚。localLeverage被setLocalLeverage不停的设置，这里另外弄了个oldvalue来保存原始值给回滚备用。
        tradingUnit[0].init.leverage = localLeverage;
        if( await beUpdateUserRevision(ServiceURLPrefix, tradingUnit[0]) ) {
            //debugger;
            console.debug(`[handleConfirmLeverage]leverage= ${tradingUnit[0].init.leverage}`)
            setLocalLeverage(localLeverage);//FIXME：到底有没有必要执行这句？执行了也还是没有马上更新显示！
        }
        else {
            //debugger;
            tradingUnit[0].init.leverage = oldvalue;
            console.error("[handleConfirmLeverage] Rollback leverage. Can't update into server.");
        }
        setIsEditingLeverage(false);//FIXME：再触发一次渲染，但是执行了也还是没有马上更新显示！
        unblockPage();
    };
    const handleCancelLeverage = () => {
        playSound();
        setIsEditingLeverage(false);
        setLocalLeverage(tradingUnit[0].init.leverage);
    };
    const handleBlurLeverage = async (e) => {
        // 如果用户点击了确认或取消按钮，则阻止 onBlur 事件的触发
        if (e.relatedTarget && (e.relatedTarget.classList.contains('confirm-button') || e.relatedTarget.classList.contains('cancel-button'))) {
            return;
        }
        handleCancelLeverage();
    }

    //========================================================================
    // 双击标题时切换tradingunit的状态enable/disable。老的db里存有一些缺少init.status字段的rev，useState初始化得到的是undefined，会导致得搞两次双击才能disable，然后就正常。
    const [localTUStatus, setLocalTUStatus] = useState(tradingUnit[0].init.status);
    const [revisionRefreshTrigger, setRevisionRefreshTrigger] = useState(0);
    const handleDoubleClickTUName = async () => {
        playSound();
        blockPage();
        // 确保 tradingUnit 存在有效的 revision
        if (!tradingUnit[0]) {
            unblockPage();
            throw new Error("A TradingUnit without any 'revision' is not allowed!");
        }
        if (!tradingUnit[0].init) {
            unblockPage();
            throw new Error("A TradingUnit head revision without 'init' is not allowed!");
        }
        const n = localTUStatus === "enable" ? "disable" : "enable";
        // 前后端操作作为一个事务处理
        let oldstatus= [];
        if( n==="enable" && tradingUnit[0].status==="end" ) {//重新激活此tradingunit
            for (let i = 0; i < tradingUnit.length; i++) {
                oldstatus[i]= tradingUnit[i].status; // 保存旧状态
                tradingUnit[i].status = "prepare"; // 恢复到prepare状态
            }
        }
        tradingUnit[0].init.status = n;
        if (await beUpdateUserRevision(ServiceURLPrefix, tradingUnit[0])) {
            console.info(`tustatus= ${tradingUnit[0].init.status}`)
            setLocalTUStatus(n);
            // OLD：本意是调用这个父接口强制更新 `tradingUnit` 或 `forceAppUpdate`，触发重新渲染，不过父那边这个的实现可能得整个重刷才能生效重绘，感觉太重了。所以只是在本组件内实现了每次重绘根据时间更新每个revision的key来达成局部刷新。
            // 更新局部刷新触发器，通知 RevisionCard 刷新
            setRevisionRefreshTrigger(prev => prev + 1);
        } else {
            if( oldstatus.length===tradingUnit.length ) {
                for (let i = 0; i < tradingUnit.length; i++) {
                    tradingUnit[i].status = oldstatus[i]; // 按顺序回滚状态
                }
            }
            tradingUnit[0].init.status = localTUStatus;
            console.error("[handleDoubleClickTUName] Rollback tu status. Can't update into server.");
        }
        unblockPage();
    };

    //========================================================================
    useEffect(() => {//被父组件强制重绘
        if( tradingUnit[0].init.status !== localTUStatus ) {
            setLocalTUStatus(tradingUnit[0].init.status);
            setRevisionRefreshTrigger(prev => prev + 1);
        }
    }, [forceRenderTrigger, tradingUnit]);//localTUStatus不在依赖里，因为它是由tradingUnit[0].init.status派生的，不需要重复触发。
    
    //========================================================================
    if (!tradingUnit) {
        return <div>未设置交易单元</div>;
    }

    //========================================================================
    //<div className="trading-unit-stream">
    //name的长度不够则补到12个，便于双击交互。FIXME：目前还没有看到太长的情况，原因未知，我的提示词好像也没限定。如果遇到太长问题再说吧。
    // <div className={`trading-unit-stream ${tradingUnit[0].init.status === "disable" ? "disabled" : ""}`}>
    return (
        <div className={`trading-unit-stream ${localTUStatus === "disable" ? "disabled" : ""}`}>
            <div>
                <div
                    onDoubleClick={handleDoubleClickTUName}
                    className="editable-field tuname"
                >
                    {(tradingUnit[0]?.init?.name || "").padEnd(12, "$")}
                </div>
                <div>

                    <div>
                        <span
                            onDoubleClick={isEditable ? handleDoubleClickExchange : null}
                            className="editable-field"
                        >
                            [{tradingUnit[0].init.exchange}]
                        </span>
                        
                        <span
                            onDoubleClick={isEditable ? handleDoubleClickInstId : null}
                            onContextMenu={handleRightClickInstId}
                            className="editable-field"
                        >
                            {tradingUnit[0].init.instId}
                        </span>
                    </div>

                    <div>
                        <span>{t(tradingUnit[0].init.positionSide)}&nbsp;</span>

                        <span 
                            onDoubleClick={isEditable ? handleDoubleClickLeverage : null}
                            className="editable-field"
                        >
                            {t("leverage")}
                        </span>
                        <span
                            onDoubleClick={isEditable ? handleDoubleClickLeverage : null}
                        >
                            {isEditingLeverage ? (
                            <span>
                                <input
                                    type="number"
                                    value={localLeverage}
                                    onChange={handleChangeLeverage}
                                    onKeyDown={handleKeyDownLeverage}
                                    onBlur={handleBlurLeverage}
                                    autoFocus
                                    step="any"
                                    className="editable-input"
                                />
                                    <button onClick={handleConfirmLeverage} className="confirm-button">✓</button>
                                    <button onClick={handleCancelLeverage} className="cancel-button">✗</button>
                            </span>
                            ) : (
                                `${tradingUnit[0].init.leverage}x`
                            )}
                        </span>
                    </div>
                </div>
            </div>

            {tradingUnit.map((revision) => {
                console.assert(tradingUnit[0].TUID === revision.TUID, "TUID 不匹配");
                return<RevisionCard
                    key={`${tradingUnit[0].TUID}-${revision.id}`}
                    revision={revision}
                    tustatus={tradingUnit[0].init.status}
                    refreshTrigger={revisionRefreshTrigger} // 传入刷新触发器
                />
            })}

        </div>
    );
};

//============================================================================
TradingUnitStreamContainer.propTypes = {
    tradingUnit: TradingUnit.isRequired,
};

//============================================================================
export default TradingUnitStreamContainer;